import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import { LogicOperator } from '@/types/logic';
import { Button, Select } from '@/ui';
import { OptionType } from '@/ui/components/Form/Select/types';
import { cn } from '@/ui/lib/utils';

import LogicElementEditor from './LogicElementEditor';
import { useQueryBuilderConfig } from './QueryBuilderContext';
import type { LogicGroupEditorProps as Props } from './types';

function LogicGroupEditor({
  group,
  fieldOptions,
  depth,
  canRemove,
  onUpdateGroup,
  onDeleteGroup,
  onAddChildGroup,
  onAddElement,
  onChangeElement,
  onDeleteElement
}: Props) {
  const { labels, allowNestedGroups, maxNestingDepth, groupActionsPosition, classNames } =
    useQueryBuilderConfig();

  const operatorChoices: OptionType<string>[] = [
    { value: LogicOperator.AND, label: labels.matchAll },
    { value: LogicOperator.OR, label: labels.matchAny }
  ];

  const canAddChildGroup =
    allowNestedGroups && (maxNestingDepth === undefined || depth < maxNestingDepth);

  const buildDefaultValue = (option: (typeof fieldOptions)[number]) =>
    // A choice field validates `value` against its allowed choices even when
    // empty, so `null` (read as [null]) is rejected -- an empty array isn't.
    option.value_choices?.length ? [] : null;

  const elementKey = (
    field: string,
    comparisonOperator: string,
    value: unknown,
    isNegated: boolean
  ) => `${field}::${comparisonOperator}::${JSON.stringify(value)}::${isNegated}`;

  const handleAddElement = () => {
    if (fieldOptions.length === 0) return;
    // The backend rejects a (field, operator, value, is_negated) tuple that
    // duplicates an existing sibling condition, so cycle through field
    // options to seed the new condition with defaults that don't already
    // exist in this group. New conditions always start non-negated.
    const existingKeys = new Set(
      group.logic_elements.map((element) =>
        elementKey(element.field, element.comparison_operator, element.value, element.is_negated)
      )
    );
    const availableOption = fieldOptions.find(
      (option) =>
        !existingKeys.has(
          elementKey(option.field, option.operators[0], buildDefaultValue(option), false)
        )
    );
    const optionToUse = availableOption ?? fieldOptions[0];

    onAddElement(group.id, {
      field: optionToUse.field,
      comparison_operator: optionToUse.operators[0],
      value: buildDefaultValue(optionToUse)
    });
  };

  const actions = (
    <div className={cn('flex gap-2', classNames.groupActions)}>
      <Button
        aria-label={labels.addCondition}
        variant="outline"
        color="secondary"
        size="sm"
        onClick={handleAddElement}>
        <FontAwesomeIcon icon={faPlus} className="mr-1" />
        {labels.addCondition}
      </Button>
      {canAddChildGroup && (
        <Button
          aria-label={labels.addGroup}
          variant="outline"
          color="neutral"
          size="sm"
          onClick={() => onAddChildGroup(group.id)}>
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          {labels.addGroup}
        </Button>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        'logic-group-editor flex flex-col gap-3 rounded-md border border-uniform-gray-300 p-3',
        classNames.group
      )}>
      <div className={cn('flex items-center justify-between gap-2', classNames.groupHeader)}>
        <div className="flex items-center gap-2">
          <span className="text-sm text-uniform-gray-600">{labels.matchPrefix}</span>
          <Select
            className="min-w-[8rem]"
            label={labels.matchPrefix}
            showLabel={false}
            options={operatorChoices}
            selectedOption={
              operatorChoices.find((option) => option.value === group.logic_operator) ?? null
            }
            onChange={(option) =>
              option && onUpdateGroup(group.id, { logic_operator: option.value as LogicOperator })
            }
            clearable={false}
          />
          <span className="text-sm text-uniform-gray-600">{labels.matchSuffix}</span>
        </div>

        {canRemove && (
          <Button
            aria-label={labels.removeGroup}
            variant="ghost"
            color="destructive"
            size="icon-sm"
            onClick={() => onDeleteGroup(group.id)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        )}
      </div>

      {groupActionsPosition === 'top' && actions}

      {group.logic_elements.map((element) => (
        <LogicElementEditor
          key={element.id}
          element={element}
          fieldOptions={fieldOptions}
          onChange={onChangeElement}
          onDelete={onDeleteElement}
        />
      ))}

      {group.logic_groups.map((childGroup) => (
        <LogicGroupEditor
          key={childGroup.id}
          group={childGroup}
          fieldOptions={fieldOptions}
          depth={depth + 1}
          canRemove
          onUpdateGroup={onUpdateGroup}
          onDeleteGroup={onDeleteGroup}
          onAddChildGroup={onAddChildGroup}
          onAddElement={onAddElement}
          onChangeElement={onChangeElement}
          onDeleteElement={onDeleteElement}
        />
      ))}

      {groupActionsPosition === 'bottom' && actions}
    </div>
  );
}

export default React.memo(LogicGroupEditor);
